import { createRoot } from 'react-dom/client';
import { RowSelection, Pagination, Table } from '@/react/libraries/FkEditTable/';
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import ToasterWrapper from '@/react/components/ToasterWrapper';
const { toast } = ToasterWrapper

import Modal from '@/react/libraries/Modal';
import Swal from '@/react/components/Swal';
import TableForm from '@/react/components/TableForm';


// constants
import { HEADER_NAMES, API_URL, BASE_URL } from '@/react/pages/qc/base/{utils}/constants';

// functions
import { initStaticPage, initTableHeaders, validateDataOnCellSave, validateAndAddKeysToCellSavePayload, generateDependentDropdown } from '@/react/pages/qc/base/{utils}/functions';


const STATUS_FOR_TAXONOMY_REVIEW = 4
const STATUS_FOR_PUBLISH = 8

const Page = ({ payload }) => {
	// page data
	const { filter_id, title, stage, type, period_map, visibleColumns } = payload


	// states
	const [selectedTableRows, setSelectedRows] = useState([])
	const [headers, setHeaders] = useState([])
	const [tableUrl, setTableUrl] = useState(`${API_URL}datatable/qcs?stage=${stage}&filter_id=${filter_id}&type=${type}&form_data={}`)


	// refs
	const tableRef = useRef(null)
	const modalRef = useRef(null)
	const paginationRef = useRef(null)

	// functions
	const checkRowSelection = () => {
		if (selectedTableRows.length === 0) {
			toast.error('Please select at least one row')
			return false
		}

		return selectedTableRows.length > 0
	}

	const previewPublication = () => {
		if (!checkRowSelection()) return

		// show a preview modal
		modalRef.current.openModal()
	}

	const publish = () => {

		const payload = {
			fk_qc_ids: selectedTableRows.map(x => x.rowId),
			status: STATUS_FOR_PUBLISH,
		}
		const api = `${BASE_URL}admin/cr/publish`
		const promise = axios.post(api, payload, {
			headers: window.HEADER_OBJ
		}).then(({ data }) => {
			if (data.status) {
				paginationRef.current.fetchData()
				modalRef.current.closeModal()

				return data.message
			} else {
				throw {
					response: {
						data: {
							message: data.message
						}
					}
				}
			}
		}).catch(err => {
			if (err.response) {
				// server made a response
				throw err.response.data.message
			} else {
				throw 'Something went wrong. Please reload your browser'
			}
		})

		toast.promise(promise, {
			loading: 'Publishing...',
			success: x => x,
			error: x => x,
		})

	}

	const previewSendToTaxonomyReviewTeam = () => {
		if (!checkRowSelection()) return

		// fire a swal!
		Swal.fire({
			title: `Send ${selectedTableRows.length} row(s) back to the review team?`,
			text: "You won't be able to revert this!",
			icon: 'warning',
			showCancelButton: true,
			cancelButtonText: 'Cancel',
			confirmButtonColor: '#3085d6',
			cancelButtonColor: '#d33',
			confirmButtonText: 'Yes, send it!'
		}).then((result) => {
			if (result.isConfirmed) {
				sendToTaxonomyReviewTeam()
			}
		})
	}

	const sendToTaxonomyReviewTeam = () => {
		const payload = {
			fk_qc_ids: selectedTableRows.map(x => x.rowId),
			status: STATUS_FOR_TAXONOMY_REVIEW,
		}
		const api = `${BASE_URL}admin/cr/send-to-stage`
		const promise = axios.post(api, payload, {
			headers: window.HEADER_OBJ
		}).then(({ data }) => {
			if (data.status) {
				paginationRef.current.fetchData()

				return data.message
			} else {
				throw {
					response: {
						data: {
							message: data.message
						}
					}
				}
			}
		}).catch(err => {
			if (err.response) {
				// server made a response
				throw err.response.data.message
			} else {
				throw 'Something went wrong. Please reload your browser'
			}
		})

		toast.promise(promise, {
			loading: 'Sending to taxonomy review team...',
			success: x => x,
			error: x => x,
		})
	}

	useEffect(() => {
		initTableHeaders({
			API_URL,
			setHeaders,
			HEADER_NAMES,
			visibleColumns,
			period_map,
		})
	}, [])
	return <>
		<div className="page-card [&_tr_td:not([data-header='Select_Row']):not([data-header='Action'])]:!pointer-events-none">
			<div>
				<h4 className="page-title">Continuous Review: Publications</h4>
				<p className='text-grey-text'>{title}</p>
			</div>
			<div>
				<TableForm onSubmit={(e, obj) => {
					const form_data = encodeURIComponent(JSON.stringify(obj));
					const url = `${API_URL}datatable/qcs?stage=${stage}&filter_id=${filter_id}&type=${type}&form_data=${form_data}`;
					setTableUrl(url)
				}} />
			</div>
			<div>
				<div className="table-controls">
					<button className="btn btn-wide btn-outline-primary" onClick={previewSendToTaxonomyReviewTeam}>Send to Taxonomy Review Team</button>
					<button className="btn btn-wide" href="#" onClick={previewPublication}>Publish</button>
				</div>
				{/* table */}
				<Table headers={headers} ref={tableRef} className={
					`!h-[calc(100dvh-456px)] !min-w-0 !overflow-auto
					[&_thead_th[data-header="Action"]]:!text-center
					[&_td[data-header="Legal_Reference_link"]]:break-all [&_td[data-header="What_to_Store"]]:min-w-[250px]`
				} />
				<RowSelection storageObject={setSelectedRows} />
				<Pagination ref={paginationRef} className={'pt-6 pr-11'} url={tableUrl} offsetQueryParameter={"start"} lengthQueryParameter={"length"} />
			</div>
		</div>

		<Modal ref={modalRef} className={`modal__content-wrapper p-2 max-w-[80vw] max-h-[80vh]`}>
			<div className="modal__content-wrapper-header">
				<h2>Unpublished Taxonomy Preview</h2>
			</div>

			<div className="modal__content-wrapper-body">
				<div className='fk-table overflow-auto max-w-[70vw]'>
					<table>
						<thead>
							<tr>
								{headers.map((col, index) => {
									if (col.heading === 'Action') return null
									return <th key={index}>{col.heading}</th>
								}
								)}
							</tr>
						</thead>
						<tbody>
							{
								selectedTableRows.map((row, index) => {
									const arrayOfCells = Array.from(row.cells)
									return (
										<tr key={index}>
											{
												arrayOfCells.map((cell, index) => {
													if (cell.dataset.header === 'Action' || cell.dataset.header === 'Select Row') return null
													if (cell.dataset.header === 'Jurisdiction')
														return (
															<td key={index} dangerouslySetInnerHTML={
																{ __html: cell.getData().text }
															}></td>
														)
													return (
														<td key={index}>{cell.getData().text}</td>
													)
												})
											}
										</tr>
									)
								})
							}
						</tbody>
					</table>
				</div>
			</div>
			<div className="modal__content-wrapper-footer">
				<button className='btn' onClick={publish}>Publish</button>
			</div>
		</Modal>
	</>
}



function initPage(selector, payload) {
	createRoot(document.querySelector(selector)).render(
		<Page payload={payload} />
	);
}

export default initPage;
